
import numeral from 'numeral';
import { financeFormat, ratioValueFormat } from '../common';
import { formatMoreThanMillion, formatAbsPercentValue, formatSmallPercent, formatSmallValue } from './dataFormatUtils';

//  numeral 注册新的格式化方式
export const numeralRegister = () => {
  // bmk 整数
  numeral.register('format', 'db_bmk', {
    regexps: {
      // 匹配任何数字
      format: /^db_bmk$/,
      // format: /(\d+)/,
      // 匹配任何非数字、非小数点、非负号的字符
      unformat: /[^0-9.-]/g
    },
    format: (value: number) => String(financeFormat(value, 0, true, '-')),
    // 用不到unformat的功能，在这里添加一个函数占位防止ts报错
    unformat: (value) => {
      // 提取数值部分
      let number = parseFloat(value);
      // 使用正则表达式查找字符串末尾的单位字符
      const unit = value.match(/[a-z]+$/i);

      if (unit !== null) {
        switch (unit[0].toLowerCase()) {
          case 'k':
            number *= 1000;
            break;
          case 'm':
            number *= 1000000;
            break;
          case 'b':
            number *= 1000000000;
            break;
          default:
            console.log((`unformat方法报错`));
        }
      }
      // 或者使用 Math.round(number) 获取更接近的整数，或者保留小数使用number本身
      return Math.floor(number);
    }
  });

  // bmk 保留一位小数
  numeral.register('format', 'db_bmk0', {
    regexps: {
      format: /^db_bmk0$/,
      unformat: /[^0-9.-]/g
    },
    format: (value: number) => String(financeFormat(value, 1, true, '-')),
    // 用不到unformat的功能，在这里添加一个函数占位防止ts报错
    unformat: (value) => Number?.(value)
  });

  // bmk 保留两位小数
  numeral.register('format', 'db_bmk00', {
    regexps: {
      format: /^db_bmk00$/,
      unformat: /[^0-9.-]/g
    },
    format: (value: number) => String(financeFormat(value, 2, true, '-')),
    // 用不到unformat的功能，在这里添加一个函数占位防止ts报错
    unformat: (value) => Number?.(value)
  });

  // bm
  numeral.register('format', 'db_bm', {
    regexps: {
      format: /^db_bm$/,
      unformat: /[^0-9.-]/g
    },
    format: (value: number) => formatMoreThanMillion(value),
    // 用不到unformat的功能，在这里添加一个函数占位防止ts报错
    unformat: (value) => Number?.(value)
  });

  // bm0
  numeral.register('format', 'db_bm0', {
    regexps: {
      format: /^db_bm0$/,
      unformat: /[^0-9.-]/g
    },
    format: (value: number) => {
      const formatter = numeral(value);
      // 小于1000000，则用千分位展示，大于则展示bmk两位小数
      return ((value < 1000000 && value > 0) || (value > -1000000 && value < 0)) ? formatter.format('0,0.0') : formatter?.format('db_bmk00');
    },
    // 用不到unformat的功能，在这里添加一个函数占位防止ts报错
    unformat: (value) => Number?.(value)
  });

  // bm00
  numeral.register('format', 'db_bm00', {
    regexps: {
      format: /^db_bm00$/,
      unformat: /[^0-9.-]/g
    },
    format: (value: number) => {
      const formatter = numeral(value);
      // 小于1000000，则用千分位展示，大于则展示bmk两位小数
      return ((value < 1000000 && value > 0) || (value > -1000000 && value < 0)) ? formatter.format('0,0.00') : formatter?.format('db_bmk00');
    },
    // 用不到unformat的功能，在这里添加一个函数占位防止ts报错
    unformat: (value) => Number?.(value)
  });

  // percent1Decimal
  numeral.register('format', 'db_percent_1_decimal', {
    regexps: {
      format: /^db_percent_1_decimal$/,
      unformat: /[^0-9.-]/g
    },
    format: (value: number) => ratioValueFormat(value, 1, true, '-', false, 3),
    // 用不到unformat的功能，在这里添加一个函数占位防止ts报错
    unformat: (value) => Number?.(value)
  });

  // percent2Decimal
  numeral.register('format', 'db_percent_2_decimal', {
    regexps: {
      format: /^db_percent_2_decimal$/,
      unformat: /[^0-9.-]/g
    },
    format: (value: number) => ratioValueFormat(value, 2, true, '-', false, 3),
    // 用不到unformat的功能，在这里添加一个函数占位防止ts报错
    unformat: (value) => Number?.(value)
  });

  // percentInt
  numeral.register('format', 'percent_int', {
    regexps: {
      format: /^percent_int$/,
      unformat: /[^0-9.-]/g
    },
    format: (value: number) => ratioValueFormat(value, 0, true, '-', false, 3),
    // 用不到unformat的功能，在这里添加一个函数占位防止ts报错
    unformat: (value) => Number?.(value)
  });

  // 小数极小值
  numeral.register('format', 'db_small_2_decimal', {
    regexps: {
      format: /^db_small_2_decimal$/,
      unformat: /[^0-9.-]/g
    },
    format: (value: number) => String(formatSmallValue({ value, type: 'decimals', decimal: 2 })),
    // 用不到unformat的功能，在这里添加一个函数占位防止ts报错
    unformat: (value) => Number?.(value)
  });

  // 百分比极小值
  numeral.register('format', 'db_small_percent_1_decimal', {
    regexps: {
      format: /^db_small_percent_1_decimal$/,
      unformat: /[^0-9.-]/g
    },
    format: (value: number) => formatSmallPercent({ value, decimal: 1, isAxis: false }),
    // 用不到unformat的功能，在这里添加一个函数占位防止ts报错
    unformat: (value) => Number?.(value)
  });

  // 百分比极小值坐标轴
  numeral.register('format', 'db_axis_small_percent_2_decimal', {
    regexps: {
      format: /^db_axis_small_percent_2_decimal$/,
      unformat: /[^0-9.-]/g
    },
    format: (value: number) => formatSmallPercent({ value, decimal: 2, isAxis: true }),
    // 用不到unformat的功能，在这里添加一个函数占位防止ts报错
    unformat: (value) => Number?.(value)
  });

  // 百分比整数（正值）
  numeral.register('format', 'db_abs_percent_int', {
    regexps: {
      format: /^db_abs_percent_int$/,
      unformat: /[^0-9.-]/g
    },
    format: (value: number) => formatAbsPercentValue(value, 0),
    // 用不到unformat的功能，在这里添加一个函数占位防止ts报错
    unformat: (value) => Number?.(value)
  });

  // 百分比1位小数（正值）
  numeral.register('format', 'db_abs_percent_1_decimal', {
    regexps: {
      format: /^db_abs_percent_1_decimal$/,
      unformat: /[^0-9.-]/g
    },
    format: (value: number) => formatAbsPercentValue(value, 1),
    // 用不到unformat的功能，在这里添加一个函数占位防止ts报错
    unformat: (value) => Number?.(value)
  });

  // 百分比2位小数（正值）
  numeral.register('format', 'db_abs_percent_2_decimal', {
    regexps: {
      format: /^db_abs_percent_2_decimal$/,
      unformat: /[^0-9.-]/g
    },
    format: (value: number) => formatAbsPercentValue(value, 2),
    // 用不到unformat的功能，在这里添加一个函数占位防止ts报错
    unformat: (value) => Number?.(value)
  });

  // value小于1000取整数，大于1000保留整数bmk格式
  numeral.register('format', 'db_int_bmk', {
    regexps: {
      format: /^db_int_bmk$/,
      unformat: /[^0-9.-]/g
    },
    format: (value: number) => {
      if (value < 1000) return numeral(value).format('0');
      return String(financeFormat(value, 0, true, '-'));
    },
    // 用不到unformat的功能，在这里添加一个函数占位防止ts报错
    unformat: (value) => Number?.(value)
  });

  // value小于1000取整数，大于1000保留一位小数bmk格式
  numeral.register('format', 'db_int_bmk0', {
    regexps: {
      format: /^db_int_bmk0$/,
      unformat: /[^0-9.-]/g
    },
    format: (value: number) => {
      if (value < 1000) return numeral(value).format('0');
      return String(financeFormat(value, 1, true, '-'));
    },
    // 用不到unformat的功能，在这里添加一个函数占位防止ts报错
    unformat: (value) => Number?.(value)
  });

  // value小于1000取整数，大于1000保留两位小数bmk格式
  numeral.register('format', 'db_int_bmk00', {
    regexps: {
      format: /^db_int_bmk00$/,
      unformat: /[^0-9.-]/g
    },
    format: (value: number) => {
      if (value < 1000) return numeral(value).format('0');
      return String(financeFormat(value, 2, true, '-'));
    },
    // 用不到unformat的功能，在这里添加一个函数占位防止ts报错
    unformat: (value) => Number?.(value)
  });
  // value小于1000取整数，大于1000保留一位小数bmk格式
  numeral.register('format', 'db_int_bmk0_1_0', {
    regexps: {
      format: /^db_int_bmk0_1_0$/,
      unformat: /[^0-9.-]/g
    },
    format: (value: number) => {
      if (value < 1000) return numeral(value).format('0');
      return String(financeFormat(value, 1, false, '-'));
    },
    // 用不到unformat的功能，在这里添加一个函数占位防止ts报错
    unformat: (value) => Number?.(value)
  });
  // value小于百万取千分位整数，大于百万保留整数bm格式
  numeral.register('format', 'db_int_bm', {
    regexps: {
      format: /^db_int_bm$/,
      unformat: /[^0-9.-]/g
    },
    format: (value: number) => {
      if ((value < 1000000 && value > 0) || (value > -1000000 && value < 0)) return numeral(value).format('0,0');
      return String(financeFormat(value, 0, true, '-'));
    },
    // 用不到unformat的功能，在这里添加一个函数占位防止ts报错
    unformat: (value) => Number?.(value)
  });

  // value小于百万取千分位整数，大于百万保留一位小数bm格式
  numeral.register('format', 'db_int_bm0', {
    regexps: {
      format: /^db_int_bm0$/,
      unformat: /[^0-9.-]/g
    },
    format: (value: number) => {
      if ((value < 1000000 && value > 0) || (value > -1000000 && value < 0)) return numeral(value).format('0,0');
      return String(financeFormat(value, 1, true, '-'));
    },
    // 用不到unformat的功能，在这里添加一个函数占位防止ts报错
    unformat: (value) => Number?.(value)
  });

  // value小于百万取千分位整数，大于百万保留两位小数bm格式
  numeral.register('format', 'db_int_bm00', {
    regexps: {
      format: /^db_int_bm00$/,
      unformat: /[^0-9.-]/g
    },
    format: (value: number) => {
      if ((value < 1000000 && value > 0) || (value > -1000000 && value < 0)) return numeral(value).format('0,0');
      return String(financeFormat(value, 2, true, '-'));
    },
    // 用不到unformat的功能，在这里添加一个函数占位防止ts报错
    unformat: (value) => Number?.(value)
  });

  // value小于1000保留两位小数，大于1000保留两位小数bmk格式（大额负数须走 bmk，不能仅用 value < 1000）
  numeral.register('format', 'db_2_bmk00', {
    regexps: {
      format: /^db_2_bmk00$/,
      unformat: /[^0-9.-]/g
    },
    format: (value: number) => {
      if (value < 1000 && value > -1000) return numeral(value).format('0.00');
      return String(financeFormat(value, 2, true, '-'));
    },
    // 用不到unformat的功能，在这里添加一个函数占位防止ts报错
    unformat: (value) => Number?.(value)
  });
};